import { useEffect } from "react";
import { useForm } from "react-hook-form";

import Modal from "../../layouts/Modal";
import useEbay from "../../data/useEbay";
import { IPart } from "../../models/Part";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";
import { NumberInput } from "../shared/Inputs/NumberInput";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { CheckboxInput } from "../shared/Inputs/CheckboxInput";
import useNotification from "../notifications/useNotifications";
import PriceFormat from "../../utils/priceFormat";
import { FormatType } from "../../models/EbayOffer";
import { computeEbayListingTitle } from "../ebayListings/EbayListingTitle";
import {
  IListingOptimisation,
  IStageListingOptimisationItem,
} from "../../models/listingOptimisation/IListingOptimisation";

interface IStageOptimisationFormFields {
  newTitle: string;
  newPrice: string;
  newFulfillmentPolicyId: string;
  newPaymentPolicyId: string;
  newReturnPolicyId: string;
  removeFirstImage: boolean;
  applyCategoryDefaults: boolean;
}

interface IStageOptimisationModalProps {
  open: boolean;
  onClose: () => void;
  part: IPart | null;
  optimisation: IListingOptimisation | undefined;
  onSubmit: (item: IStageListingOptimisationItem) => void;
  isLoading: boolean;
}

// Edits the staged (not live) values for a single listing. Staged values are only
// applied when the listing is sent to be relisted - nothing here touches eBay.
export default function StageOptimisationModal({
  open,
  onClose,
  part,
  optimisation,
  onSubmit,
  isLoading,
}: IStageOptimisationModalProps) {
  const { addNotification } = useNotification();
  const { policies } = useEbay();

  const offer = part?.offers?.at(0);
  const currentPrice =
    offer?.format === FormatType.Auction
      ? offer?.auctionStartPrice
      : offer?.buyItNowPrice;

  const { register, setValue, watch, handleSubmit, reset } =
    useForm<IStageOptimisationFormFields>({
      defaultValues: {
        newTitle: "",
        newPrice: "",
        newFulfillmentPolicyId: "",
        newPaymentPolicyId: "",
        newReturnPolicyId: "",
        removeFirstImage: false,
        applyCategoryDefaults: false,
      },
    });

  // Prefill with the staged values when re-editing, falling back to the current
  // offer values so the form always shows the full state that would be relisted.
  useEffect(() => {
    if (!open || !part) return;

    reset({
      newTitle: optimisation?.newTitle ?? offer?.title ?? "",
      newPrice: (optimisation?.newPrice ?? currentPrice)?.toString() ?? "",
      newFulfillmentPolicyId: optimisation?.newFulfillmentPolicyId ?? offer?.fulfillmentPolicyId ?? "",
      newPaymentPolicyId: optimisation?.newPaymentPolicyId ?? offer?.paymentPolicyId ?? "",
      newReturnPolicyId: optimisation?.newReturnPolicyId ?? offer?.returnPolicyId ?? "",
      removeFirstImage: optimisation?.removeFirstImage ?? false,
      applyCategoryDefaults: optimisation?.applyCategoryDefaults ?? false,
    });
  }, [open, part]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = (data: IStageOptimisationFormFields) => {
    if (!part) return;

    // Only stage values that differ from the current offer - null means
    // "leave unchanged" when the listing is relisted.
    const title = data.newTitle?.trim();
    const price = parseFloat(data.newPrice);

    const item: IStageListingOptimisationItem = {
      partDataId: part.id,
      newTitle: title && title !== offer?.title ? title : null,
      newPrice: !isNaN(price) && price > 0 && price !== currentPrice ? price : null,
      newFulfillmentPolicyId:
        data.newFulfillmentPolicyId && data.newFulfillmentPolicyId !== offer?.fulfillmentPolicyId
          ? data.newFulfillmentPolicyId
          : null,
      newPaymentPolicyId:
        data.newPaymentPolicyId && data.newPaymentPolicyId !== offer?.paymentPolicyId
          ? data.newPaymentPolicyId
          : null,
      newReturnPolicyId:
        data.newReturnPolicyId && data.newReturnPolicyId !== offer?.returnPolicyId
          ? data.newReturnPolicyId
          : null,
      removeFirstImage: data.removeFirstImage,
      applyCategoryDefaults: data.applyCategoryDefaults,
    };

    const hasChanges =
      item.newTitle != null ||
      item.newPrice != null ||
      item.newFulfillmentPolicyId != null ||
      item.newPaymentPolicyId != null ||
      item.newReturnPolicyId != null ||
      item.removeFirstImage === true ||
      item.applyCategoryDefaults === true;

    if (!hasChanges && !optimisation) {
      addNotification({ primaryText: "No changes to stage", variant: "warn" });
      return;
    }

    onSubmit(item);
  };

  return (
    <Modal open={open} setOpen={handleClose} width="max-w-5xl">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="space-y-4">
          <h2 className="text-lg font-medium leading-6 text-gray-900">Stage Listing Changes</h2>
          <p className="text-sm text-gray-600">
            These changes are saved against the listing but not sent to eBay. They are applied
            when the listing is sent to be relisted.
          </p>

          {offer && (
            <div className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
              <span className="font-medium text-gray-700">Currently live:</span>{" "}
              {offer.title} — {PriceFormat(currentPrice)}
            </div>
          )}

          <div className="flex items-end gap-2">
            <div className="grow">
              <TextInput
                label={"Title"}
                register={register}
                registerName={"newTitle"}
                cols={"sm:col-span-2"}
                maxLength={80}
              />
            </div>
            <ButtonInput
              label={"Use Wilma Title"}
              isSubmit={false}
              onClick={() => part && setValue("newTitle", computeEbayListingTitle(part, null, true))}
              classes={"whitespace-nowrap"}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <NumberInput label={"Price"} register={register} registerName={"newPrice"} min={0} />
            <SelectInput label={"Shipping Policy"} register={register} registerName={"newFulfillmentPolicyId"} options={policies.data?.data.fulfilmentPolicies || []} setValue={setValue} watch={watch} />
            <SelectInput label={"Payment Policy"} register={register} registerName={"newPaymentPolicyId"} options={policies.data?.data.paymentPolicies || []} setValue={setValue} watch={watch} />
            <SelectInput label={"Returns Policy"} register={register} registerName={"newReturnPolicyId"} options={policies.data?.data.returnPolicies || []} setValue={setValue} watch={watch} />
          </div>

          {/* Relist-time actions - applied server-side just before the listing republishes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-md bg-gray-50 px-3 py-2">
            <CheckboxInput
              label={"Remove first image on relist"}
              register={register}
              registerName={"removeFirstImage"}
            />
            <CheckboxInput
              label={"Apply category defaults on relist"}
              register={register}
              registerName={"applyCategoryDefaults"}
            />
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t">
            <ButtonInput label={"Cancel"} isSubmit={false} onClick={handleClose} classes={"!bg-gray-200 hover:!bg-gray-300 !text-gray-700"} />
            <ButtonInput label={"Stage Changes"} isSubmit={true} onClick={undefined} classes={undefined} isLoading={isLoading} />
          </div>
        </div>
      </form>
    </Modal>
  );
}
